/*
 * VH-103 — Print + Download PDF actions for a single recipe.
 *
 * - handlePrint(): tags <body> with `printing-recipe` so the scoped @media
 *   print stylesheet (print-recipe.css) hides the SPA chrome and promotes
 *   the printable view, then calls window.print(). The body class is
 *   cleared on the `afterprint` event.
 * - handleDownloadPdf(): rasterises the supplied printable view ref via
 *   html2canvas-pro, then slices the canvas across A4/Letter PDF pages with
 *   jsPDF. Mirrors components/profile/ComprehensiveAnalysisActions.jsx
 *   (VH-83) — the comments there explain the slice maths.
 *
 * Why html2canvas-pro and not html2canvas: Tailwind 4's default palette is
 * authored in oklch(), and browsers return oklch() verbatim from
 * getComputedStyle rather than converting to rgb. html2canvas@1.4.1 (final
 * release, 2021) has no oklch parser and throws
 * `Attempting to parse an unsupported color function "oklch"`, so every PDF
 * export failed the moment a default-palette colour (text-gray-600 here) was
 * in the captured subtree. html2canvas-pro is the maintained, API-compatible
 * fork that understands oklch/lab/lch/color(). Printing was never affected —
 * window.print() uses the browser's own renderer.
 *
 * No backend calls — the user is already authenticated to view the
 * recipe on screen, so generating from that DOM is implicitly authorised.
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { withLightTheme } from '../../utils/captureLight'
import {
  PrinterIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import {
  getPaperSize,
  applyPrintPaper,
  clearPrintPaper,
} from '../../utils/printPaper'

const PDF_MARGIN_MM = 10
// How far above the ideal page boundary to look for a blank row to cut at.
// Roughly one line of body text at scale=2 (~24 px) → 80 px is generous.
const PAGE_BREAK_SEARCH_PX = 80


function todayIso() {
  return new Date().toISOString().split('T')[0]
}

/**
 * Scan a row of pixel data for any non-white pixel.
 * `pixels` is the RGBA buffer for ONE row (length = width * 4).
 */
function isRowBlank(pixels) {
  const len = pixels.length
  for (let i = 0; i < len; i += 4) {
    const r = pixels[i]
    const g = pixels[i + 1]
    const b = pixels[i + 2]
    const a = pixels[i + 3]
    // Treat fully-transparent OR near-white as blank.
    if (a > 0 && (r < 248 || g < 248 || b < 248)) return false
  }
  return true
}

/**
 * Walk back from `idealY` looking for a near-blank row to cut at.
 * Returns the chosen y-coordinate. Falls back to `idealY` if no blank
 * row is found within the search window.
 *
 * `imageData` is the full canvas's ImageData; we read 1 row at a time
 * via subarray to keep allocations cheap.
 */
function findCleanCutY(imageData, idealY, width, maxLookback) {
  const data = imageData.data
  const start = Math.max(0, idealY - maxLookback)
  const rowBytes = width * 4
  for (let y = idealY; y >= start; y--) {
    const offset = y * rowBytes
    const row = data.subarray(offset, offset + rowBytes)
    if (isRowBlank(row)) return y
  }
  return idealY
}

export default function RecipePrintActions({ recipe, printableRef }) {
  const { t, i18n } = useTranslation()
  const [isExporting, setIsExporting] = useState(false)

  const disabled = !recipe || isExporting

  // Restore body state if the user navigates away from the print preview.
  useEffect(() => {
    const handleAfterPrint = () => {
      document.body.classList.remove('printing-recipe')
      document.body.removeAttribute('data-print-paper')
      clearPrintPaper()
    }
    window.addEventListener('afterprint', handleAfterPrint)
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint)
      // Unmounting mid-print would otherwise strand the injected @page rule
      // and leak the chosen size into unrelated prints.
      clearPrintPaper()
    }
  }, [])

  const handlePrint = () => {
    if (!recipe) return
    const paper = getPaperSize(i18n.language)
    document.body.classList.add('printing-recipe')
    document.body.setAttribute('data-print-paper', paper)
    // The @page size has to be injected — it cannot be selector-scoped in CSS.
    applyPrintPaper(paper)
    setTimeout(() => {
      window.print()
    }, 0)
  }

  const handleDownloadPdf = async () => {
    if (!recipe || !printableRef?.current) return
    setIsExporting(true)
    try {
      // Lazy-load the PDF libs so they don't sit in the main bundle.
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas-pro'),
        import('jspdf'),
      ])

      // VH-133: capture in light colours whatever theme is on screen — a
      // dark-mode recipe PDF is a black page nobody can print.
      const canvas = await withLightTheme(printableRef.current, () =>
        html2canvas(printableRef.current, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
        })
      )

      const paper = getPaperSize(i18n.language)
      const pdf = new jsPDF('p', 'mm', paper)
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const imgWidth = canvas.width
      const imgHeight = canvas.height

      const usableWidth = pdfWidth - PDF_MARGIN_MM * 2
      const usableHeight = pdfHeight - PDF_MARGIN_MM * 2
      const ratio = usableWidth / imgWidth
      const sliceHeightPx = usableHeight / ratio

      // Pull the full canvas pixel data once so the page-break scanner can
      // find blank rows. If the canvas is tainted (cross-origin image without
      // CORS), getImageData throws SecurityError — fall back to dumb slicing.
      let imageData = null
      try {
        const fullCtx = canvas.getContext('2d')
        imageData = fullCtx.getImageData(0, 0, canvas.width, canvas.height)
      } catch (e) {
         
        console.warn(
          'Canvas is tainted; falling back to fixed-height PDF slicing',
          e
        )
      }

      let cursor = 0
      let pageIndex = 0
      while (cursor < imgHeight) {
        if (pageIndex > 0) pdf.addPage()

        const idealEnd = Math.min(cursor + sliceHeightPx, imgHeight)
        let sliceEnd = idealEnd
        // Snap up to a clean row, but only for non-final pages and only when
        // we have pixel access. Don't search if the page would end up empty
        // (sliceEnd ≤ cursor + half-slice → fall back to ideal).
        if (imageData && idealEnd < imgHeight) {
          const candidate = findCleanCutY(
            imageData,
            Math.floor(idealEnd),
            imgWidth,
            PAGE_BREAK_SEARCH_PX
          )
          if (candidate > cursor + sliceHeightPx / 2) {
            sliceEnd = candidate
          }
        }
        const sourceHeight = sliceEnd - cursor

        const pageCanvas = document.createElement('canvas')
        pageCanvas.width = imgWidth
        pageCanvas.height = sourceHeight
        const ctx = pageCanvas.getContext('2d')
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, imgWidth, sourceHeight)
        ctx.drawImage(
          canvas,
          0, cursor, imgWidth, sourceHeight,
          0, 0, imgWidth, sourceHeight
        )

        const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.92)
        pdf.addImage(
          pageImgData,
          'JPEG',
          PDF_MARGIN_MM,
          PDF_MARGIN_MM,
          usableWidth,
          sourceHeight * ratio
        )

        cursor = sliceEnd
        pageIndex += 1
      }

      pdf.setFontSize(8)
      pdf.setTextColor(128, 128, 128)
      pdf.text(
        `VitaHub — ${new Date().toLocaleDateString()}`,
        pdfWidth / 2,
        pdfHeight - 4,
        { align: 'center' }
      )

      pdf.save(`recipe-${recipe.id}-${todayIso()}.pdf`)
    } catch (err) {
       
      console.error('Recipe PDF export failed', err)
      toast.error(t('recipes.print.errorPdf'))
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div
      className="recipe-print-actions no-print flex items-center gap-2"
      data-testid="recipe-print-actions"
    >
      <button
        type="button"
        onClick={handlePrint}
        disabled={disabled}
        className="btn-secondary flex items-center gap-1 text-sm"
        aria-label={t('recipes.print.print')}
        data-testid="recipe-print-btn"
      >
        <PrinterIcon className="h-4 w-4" />
        <span>{t('recipes.print.print')}</span>
      </button>
      <button
        type="button"
        onClick={handleDownloadPdf}
        disabled={disabled}
        className="btn-secondary flex items-center gap-1 text-sm"
        aria-label={t('recipes.print.downloadPdf')}
        data-testid="recipe-pdf-btn"
      >
        {isExporting ? (
          <>
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
            <span>{t('recipes.print.generating')}</span>
          </>
        ) : (
          <>
            <ArrowDownTrayIcon className="h-4 w-4" />
            <span>{t('recipes.print.downloadPdf')}</span>
          </>
        )}
      </button>
    </div>
  )
}
